import { evaluateEcddFormGates, isForeignPep } from './EcddStructuredForm'

export const TRIGGER_LABELS = {
  alert: 'Rule / alert',
  pep: 'PEP / screening hit',
  large_payout: 'Large payout',
  risk: 'High risk rating',
  manual: 'Manual observation',
  postsmr: 'Post-SMR',
  unusual: 'Unusual / complex activity',
}

export const SOF_RESP = { coop: 'Cooperative', partial: 'Partial', refused: 'Refused / evasive' }
export const SOF_ASSESS = { sat: 'Satisfactory', unsat: 'Unsatisfactory', further: 'Requires further enquiry' }
export const SCR_DISP = { false: 'False positive', true: 'True positive' }
export const PEP_CLASS = {
  domestic: 'Domestic',
  foreign: 'Foreign',
  intorg: 'International organisation',
  notpep: 'Not a PEP (other list)',
}
export const DECISION_LABELS = {
  satisfactory: 'Close — Satisfactory',
  enhanced_monitoring: 'Enhanced Monitoring',
  escalated_smr: 'Escalate → SMR',
}

export const INDICATOR_LABELS = {
  structuring: 'Structuring below thresholds',
  'minimal-play': 'Minimal play / cash-through',
  'sof-mismatch': 'Activity inconsistent with profile',
  refusal: 'Reluctance / refusal to provide information',
  thirdparty: 'Third-party involvement',
  'staff-influence': 'Attempts to influence staff',
  'no-indicators': 'No indicators',
}

function dash(v) {
  if (v == null || String(v).trim() === '') return null
  if (v === true) return 'Yes'
  if (v === false) return 'No'
  return String(v)
}

function yesNo(v) {
  if (v === 'yes') return 'Yes'
  if (v === 'no') return 'No'
  return dash(v)
}

function row(label, value) {
  const v = value == null || value === '' ? null : value
  if (v == null) return null
  return { label, value: v }
}

function docName(d) {
  return typeof d === 'string' ? d : d?.file_name || 'file'
}

/**
 * Structured sections for ECDD read-only preview (modal + print).
 */
export function getEcddPreviewSections({
  record,
  formAnswers: a = {},
  decision,
  decisionRationale,
  gates,
  dynamicFormResponses = [],
}) {
  const evalGates = gates || evaluateEcddFormGates(a || {})
  const gateMap = Object.fromEntries((evalGates.gates || []).map((g) => [g.id, g.ok]))
  const sofReq = isForeignPep(a) ? 'yes' : a?.sofRequired
  const indicators = Array.isArray(a?.indicators) ? a.indicators : []
  const evidenceItems = Array.isArray(a?.sofEvidenceItems) ? a.sofEvidenceItems : []
  const findings = Array.isArray(a?.sofFindings) ? a.sofFindings : []
  const sofDocs = Array.isArray(a?.sofDocs) ? a.sofDocs : []
  const actDocs = Array.isArray(a?.actDocs) ? a.actDocs : []

  const sections = [
    {
      number: 1,
      title: 'Case',
      ok: null,
      rows: [
        row('ECDD ID', record?.id),
        row('Status', record?.status),
        row('Risk', record?.risk != null ? String(record.risk) : null),
        row('Assigned', record?.assignedTo),
        row('Venue', record?.venue || record?.venueName),
      ].filter(Boolean),
    },
    {
      number: 2,
      title: 'Trigger',
      ok: gateMap.trigger,
      rows: [
        row('Type', TRIGGER_LABELS[a?.triggerType] || a?.triggerType),
        row('Date', a?.triggerDate),
        row('Description', a?.triggerDesc),
        row('Linked alert', a?.linkedAlertId),
        row('Observer', a?.observer),
        row('Observation date', a?.obsDate),
        row('Prior SMR', a?.priorSmr),
      ].filter(Boolean),
    },
    {
      number: 3,
      title: 'KYC / identity',
      ok: gateMap.kyc,
      rows: [
        row('KYC current', yesNo(a?.kycCurrent)),
        ...(a?.kycCurrent === 'no'
          ? [
              row('Document', [a?.docType, a?.docNum].filter(Boolean).join(' · ') || null),
              row('Expiry', a?.docExp),
              row('Method', a?.verMethod),
              row('Result', a?.verResult),
              row('Verified', a?.verDate),
            ].filter(Boolean)
          : []),
        row('Occupation', a?.occupation),
        row('Activity consistent', yesNo(a?.occConsistent)),
        row('Customer type', a?.customerType),
      ].filter(Boolean),
    },
    {
      number: 4,
      title: 'Source of funds / wealth',
      ok: gateMap.sof,
      rows:
        sofReq === 'no'
          ? [row('Enquiry required', yesNo(sofReq)), row('Reason not required', a?.sofNoReason)].filter(Boolean)
          : sofReq === 'yes'
            ? [
                row('Enquiry required', yesNo(sofReq)),
                row('Method', a?.sofMethod),
                row('Date', a?.sofDate),
                row('By', a?.sofBy),
                row(
                  'Source of funds',
                  a?.sofCat === 'other' ? `Other — ${a?.sofOtherDetail || ''}` : a?.sofCat
                ),
                row('Source of wealth', a?.sowCat),
                row('Evidence summary', a?.sofEvidence),
                evidenceItems.length > 0
                  ? row('Evidence notes', evidenceItems.map((e) => `• ${e.text}`).join('\n'))
                  : null,
                findings.length > 0
                  ? row('Findings', findings.map((f) => `• [${f.source}] ${f.summary}`).join('\n'))
                  : null,
                row('Patron response', SOF_RESP[a?.sofResp] || a?.sofResp),
                row('CO assessment', SOF_ASSESS[a?.sofAssess] || a?.sofAssess),
                row('Override', a?.satOverride),
                sofDocs.length > 0 ? row('Documents', sofDocs.map(docName).join('\n')) : null,
              ].filter(Boolean)
            : [row('Enquiry required', yesNo(sofReq))].filter(Boolean),
    },
    {
      number: 5,
      title: 'Activity analysis',
      ok: gateMap.activity,
      rows: a?.noActivityToLink
        ? [row('Activity', 'No activity to link')]
        : [
            row('Period', [a?.actFrom, a?.actTo].filter(Boolean).join(' → ') || null),
            row('Summary', a?.actSummary),
            row('Consistent with SoF', yesNo(a?.actConsistent)),
            row('Inconsistency notes', a?.inconNotes),
            indicators.length > 0
              ? row('Indicators', indicators.map((i) => INDICATOR_LABELS[i] || i).join('; '))
              : a?.noIndicators && !indicators.some((i) => i !== 'no-indicators')
                ? row('Indicators', 'No indicators')
                : null,
            actDocs.length > 0 ? row('Documents', actDocs.map(docName).join('\n')) : null,
          ].filter(Boolean),
    },
    {
      number: 6,
      title: 'Patron engagement',
      ok: gateMap.engage,
      rows: [
        row('Engaged', yesNo(a?.engaged)),
        ...(a?.engaged === 'yes'
          ? [
              row('Date', a?.engDate),
              row('By', a?.engBy),
              row('Mode', a?.engMode),
              row('Tipping-off declared', a?.tipDecl ? 'Yes' : null),
            ].filter(Boolean)
          : []),
        a?.tipDeclPostSmr ? row('Post-SMR tipping-off', 'Declared') : null,
      ].filter(Boolean),
    },
    {
      number: 7,
      title: 'Screening refresh',
      ok: gateMap.screen,
      rows: [
        row('Re-screen date', a?.scrDate),
        row(
          'Result',
          a?.scrResult === 'clear' ? 'Clear' : a?.scrResult === 'hit' ? 'Hit' : a?.scrResult
        ),
        row('Linked check', a?.scrRef ? `SCR-${a.scrRef}` : null),
        row('Disposition', SCR_DISP[a?.scrDisp] || a?.scrDisp),
        row('PEP class', PEP_CLASS[a?.pepClass] || a?.pepClass),
      ].filter(Boolean),
    },
    {
      number: 'D',
      title: 'Decision',
      ok: null,
      rows: [
        row('Outcome', DECISION_LABELS[decision] || decision),
        row('Rationale', decisionRationale),
      ].filter(Boolean),
    },
  ]

  if (Array.isArray(dynamicFormResponses) && dynamicFormResponses.length > 0) {
    sections.push({
      number: 'F',
      title: 'Additional form responses',
      ok: null,
      forms: dynamicFormResponses.map((r) => ({
        formName: r.formName || 'Form',
        submittedAt: r.submittedAt,
        submissionRef: r.submissionRef,
        rows: (r.rows || []).filter((x) => x?.label && x?.value != null),
      })),
    })
  }

  return { sections, evalGates }
}
